import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Search, Filter, Plus, Download, Copy, Eye, Calendar, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusOptions = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'brouillon', label: 'Brouillon' },
  { value: 'envoyé', label: 'Envoyé' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'validé', label: 'Validé' },
  { value: 'en_production', label: 'En production' },
  { value: 'terminé', label: 'Terminé' },
  { value: 'annulé', label: 'Annulé' },
];

const EVENT_TYPE_LABELS = {
  'mesure': 'Relevé prévu',
  'livraison_pose': 'Livraison & Pose',
  'livraison': 'Livraison & Pose',
  'pose': 'Livraison & Pose',
  'autre': 'Autre'
};

export default function QuotesHistory() {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const isAdminView = location.pathname.startsWith('/admin');
  
  const [quotes, setQuotes] = useState([]);
  const [planningEvents, setPlanningEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const [quotesRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/quotes`),
        axios.get(`${API_URL}/planning/events`)
      ]);
      setQuotes(quotesRes.data);
      
      // Group planning events by quote_id
      const eventsByQuote = {};
      eventsRes.data.forEach(event => {
        if (event.quote_id) {
          if (!eventsByQuote[event.quote_id]) {
            eventsByQuote[event.quote_id] = [];
          }
          eventsByQuote[event.quote_id].push(event);
        }
      });
      setPlanningEvents(eventsByQuote);
    } catch (error) {
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const getQuotePlanning = (quoteId) => {
    const events = planningEvents[quoteId];
    if (!events || events.length === 0) return null;
    
    // Get the most relevant upcoming event (validated first, then proposed)
    const sortedEvents = [...events].sort((a, b) => {
      // Validated events first
      if (a.status === 'validated' && b.status !== 'validated') return -1;
      if (b.status === 'validated' && a.status !== 'validated') return 1;
      // Then by date
      const dateA = new Date(a.validated_date || a.proposed_date);
      const dateB = new Date(b.validated_date || b.proposed_date);
      return dateA - dateB;
    });
    
    return sortedEvents[0];
  };

  const formatPlanningDisplay = (event) => {
    if (!event) return { text: 'Aucun', color: 'text-white/40' };
    
    const label = EVENT_TYPE_LABELS[event.event_type] || event.event_label || 'Planifié';
    const date = event.validated_date || event.proposed_date;
    const dateStr = date ? new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';
    const isValidated = event.status === 'validated';
    
    return {
      text: `${label}`,
      date: dateStr,
      color: isValidated ? 'text-green-400' : 'text-yellow-400',
      isValidated
    };
  };

  const handleDuplicate = async (quoteId) => {
    try {
      const response = await axios.post(`${API_URL}/quotes/${quoteId}/duplicate`);
      toast.success(`Devis dupliqué: ${response.data.quote_number}`);
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDownloadPDF = async (quoteId, quoteNumber) => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${quoteId}/pdf`);
      const { pdf_base64, filename } = response.data;
      
      // Download
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf_base64}`;
      link.download = filename;
      link.click();
      
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.client_info?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.material_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusLabel = (status) => {
    const option = statusOptions.find(o => o.value === status);
    return option?.label || status;
  };

  const basePath = isAdminView ? '/admin' : '/dashboard';

  return (
    <div className="space-y-6" data-testid="quotes-history">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isAdminView ? 'Tous les devis' : 'Mes devis'}
          </h1>
          <p className="text-white/50 mt-1">
            {filteredQuotes.length} devis trouvé{filteredQuotes.length > 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/configurateur"
          className="btn-primary flex items-center gap-2 justify-center"
          data-testid="create-quote-btn"
        >
          <Plus className="w-4 h-4" />
          Nouveau devis
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un devis..."
            className="pl-10 bg-[#1C1C1C] border-white/10 text-white rounded-none"
            data-testid="search-quotes"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-[#1C1C1C] border-white/10 text-white rounded-none" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2 text-white/40" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1C1C1C] border-white/10">
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-white hover:bg-white/10">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="card-dashboard text-center py-16">
          <FileText className="w-16 h-16 text-white/10 mx-auto mb-4" />
          <p className="text-white/50 text-lg">Aucun devis trouvé</p>
          {searchTerm || statusFilter !== 'all' ? (
            <button
              onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
              className="text-[#D4AF37] text-sm mt-2 hover:underline"
            >
              Réinitialiser les filtres
            </button>
          ) : (
            <Link to="/configurateur" className="btn-primary mt-4 inline-flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Créer un devis
            </Link>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">N° Devis</th>
                {isAdminView && (
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Pro</th>
                )}
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Client</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Matière</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Surface</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Total TTC</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Statut</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Planification
                  </div>
                </th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Date</th>
                <th className="text-right text-white/50 text-xs uppercase tracking-wider py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotes.map((quote, index) => (
                <motion.tr
                  key={quote.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  <td className="py-4 px-4">
                    <Link 
                      to={`${basePath}/devis/${quote.id}`}
                      className="text-[#D4AF37] hover:underline font-medium"
                      data-testid={`quote-link-${quote.quote_number}`}
                    >
                      {quote.quote_number}
                    </Link>
                  </td>
                  {isAdminView && (
                    <td className="py-4 px-4 text-white/70 text-sm">{quote.pro_company_name}</td>
                  )}
                  <td className="py-4 px-4">
                    <div>
                      <p className="text-white">{quote.client_info?.name}</p>
                      <p className="text-white/40 text-xs">{quote.client_info?.city}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div>
                      <p className="text-white/70">{quote.material_name}</p>
                      <p className="text-white/40 text-xs">{quote.thickness}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-white/70">
                    {quote.total_surface_m2?.toFixed(2)} m²
                  </td>
                  <td className="py-4 px-4 text-white font-semibold">
                    {quote.total_ttc?.toLocaleString('fr-FR')} €
                  </td>
                  <td className="py-4 px-4">
                    <span className={`status-${quote.status} px-3 py-1 text-xs uppercase tracking-wider`}>
                      {getStatusLabel(quote.status)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    {(() => {
                      const event = getQuotePlanning(quote.id);
                      const display = formatPlanningDisplay(event);
                      return (
                        <div className="text-sm">
                          <span className={display.color}>{display.text}</span>
                          {display.date && (
                            <span className="text-white/40 ml-1 text-xs">{display.date}</span>
                          )}
                          {event && display.isValidated && (
                            <span className="ml-1 text-green-500 text-xs">✓</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="py-4 px-4 text-white/50 text-sm">
                    {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`${basePath}/devis/${quote.id}`}
                        className="p-2 text-white/40 hover:text-white transition-colors"
                        title="Voir le détail"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDownloadPDF(quote.id, quote.quote_number)}
                        className="p-2 text-white/40 hover:text-[#D4AF37] transition-colors"
                        title="Télécharger PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(quote.id)}
                        className="p-2 text-white/40 hover:text-[#D4AF37] transition-colors"
                        title="Dupliquer"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
